import React, { useState } from 'react';
import { X, FileText, Link, Send } from 'lucide-react';
import { applyForJob } from '../../../services/jobService';

const ApplicationModal = ({ job, isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    cover_letter: '',
    resume_url: '',
    portfolio_url: '',
    additional_documents: [],
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      await applyForJob({
        job_id: job?.id,
        cover_letter: formData?.cover_letter,
        resume_url: formData?.resume_url || null,
        portfolio_url: formData?.portfolio_url || null,
        additional_documents: formData?.additional_documents?.length > 0 
          ? formData?.additional_documents 
          : null,
      });
      
      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      setError(err?.message || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Apply for Position</h2>
              <p className="text-gray-600 mt-1">{job?.title}</p>
              <p className="text-sm text-gray-500">
                {job?.user_profiles?.organization || job?.user_profiles?.full_name}
              </p>
            </div>
            <button
              onClick={onClose}
              className="ml-4 p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Success Message */}
        {success && (
          <div className="p-6 bg-green-50 border-b border-green-200">
            <div className="text-green-800">
              <h3 className="font-medium">Application Submitted Successfully!</h3>
              <p className="text-sm mt-1">
                Your application has been sent to the employer. You should hear back soon.
              </p>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="p-6 bg-red-50 border-b border-red-200">
            <div className="text-red-800">
              <h3 className="font-medium">Application Failed</h3>
              <p className="text-sm mt-1">{error}</p>
            </div>
          </div>
        )}

        {/* Form */}
        {!success && (
          <form onSubmit={handleSubmit} className="p-6">
            <div className="space-y-6">
              {/* Cover Letter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cover Letter *
                </label>
                <textarea
                  required
                  rows={6}
                  value={formData?.cover_letter}
                  onChange={(e) => handleInputChange('cover_letter', e?.target?.value)}
                  placeholder="Tell us why you're interested in this position and what makes you a great candidate..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Minimum 50 characters required
                </p>
              </div>

              {/* Resume URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Resume/CV Link
                  <span className="text-gray-500 font-normal ml-1">(Optional)</span>
                </label>
                <div className="relative">
                  <Link className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="url"
                    value={formData?.resume_url}
                    onChange={(e) => handleInputChange('resume_url', e?.target?.value)}
                    placeholder="https://drive.google.com/your-resume..."
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Link to your resume on Google Drive, Dropbox, or similar service
                </p>
              </div>

              {/* Portfolio URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Portfolio/Work Samples
                  <span className="text-gray-500 font-normal ml-1">(Optional)</span>
                </label>
                <div className="relative">
                  <Link className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="url"
                    value={formData?.portfolio_url}
                    onChange={(e) => handleInputChange('portfolio_url', e?.target?.value)}
                    placeholder="https://yourportfolio.com or GitHub link..."
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Link to your portfolio, GitHub, or relevant work samples
                </p>
              </div>

              {/* Additional Documents */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Document Links
                  <span className="text-gray-500 font-normal ml-1">(Optional)</span>
                </label>
                <div className="space-y-2">
                  {formData?.additional_documents?.map((doc, index) => (
                    <div key={index} className="flex gap-2">
                      <div className="relative flex-1">
                        <Link className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                          type="url"
                          value={doc}
                          onChange={(e) => {
                            const newDocs = [...formData?.additional_documents];
                            newDocs[index] = e?.target?.value;
                            handleInputChange('additional_documents', newDocs);
                          }}
                          placeholder="https://link-to-document..."
                          className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const newDocs = formData?.additional_documents?.filter((_, i) => i !== index);
                          handleInputChange('additional_documents', newDocs);
                        }}
                        className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  
                  <button
                    type="button"
                    onClick={() => {
                      handleInputChange('additional_documents', [...(formData?.additional_documents || []), '']);
                    }}
                    className="w-full px-3 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-gray-400 hover:text-gray-700 flex items-center justify-center gap-2"
                  >
                    <FileText className="w-4 h-4" />
                    Add Document Link
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Add links to certifications, transcripts, or other relevant documents
                </p>
              </div>

              {/* Job Requirements Reminder */}
              {job?.requirements?.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-blue-900 mb-2">Job Requirements Reminder:</h4>
                  <ul className="text-xs text-blue-800 space-y-1">
                    {job?.requirements?.slice(0, 3)?.map((requirement, index) => (
                      <li key={index} className="flex items-start">
                        <span className="w-1 h-1 bg-blue-600 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                        {requirement}
                      </li>
                    ))}
                    {job?.requirements?.length > 3 && (
                      <li className="text-blue-600">
                        +{job?.requirements?.length - 3} more requirements...
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>

            {/* Submit Button */}
            <div className="flex gap-3 mt-8 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || !formData?.cover_letter || formData?.cover_letter?.length < 50}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Submit Application
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ApplicationModal;